import encodeUrl from 'root-core/utils/encode/url.js'
import * as sellingCompanyRegions from 'root-config/sellingCompanyRegions.js'
import {dateJSONReviver} from '@booking-services/json-reviver'

function HttpException (message, statusCode) {
  this.name = 'HttpException'
  this.message = message
  this.statusCode = statusCode
}
HttpException.prototype = Error.prototype

class Api {
  constructor (options) {
    this.onSuccess = typeof options == 'object' &&
      options.hasOwnProperty('onSuccess')
      ? options.onSuccess
      : () => {}
    this.onError = typeof options == 'object' &&
      options.hasOwnProperty('onError')
      ? options.onError
      : () => {}

    this.client = options.http({webUrl: options.webUrl})
  }

  get (url, options) {
    return this.makeRequest('get', url, options)
  }

  post (url, body, options) {
    return this.makeRequest('post', url, body, options)
  }

  put (url, body, options) {
    return this.makeRequest('put', url, body, options)
  }

  makeRequest (method, url, body, options) {
    return this.client
      [method](url, body, options)
      .then(response => {
        this.onSuccess()
        return response
      })
      .catch(response => {
        this.onError(response.body || response)
        throw new HttpException(response, response.statusCode)
      })
  }

  departure (sellingCompanyCode, tourCode, departureCode) {
    return this.get(
      encodeUrl`${sellingCompanyCode}/${tourCode}/${departureCode}`
    )
  }

  content (sellingCompanyCode, tourCode, departureCode) {
    return this.get(
      encodeUrl`content/${sellingCompanyCode}/${tourCode}/${departureCode}`,
      {
        exceptions: ({statusCode}) => statusCode > 400 && statusCode != 404,
      }
    )
  }

  allPromotions (sellingCompanyCode, tourCode, departureCode) {
    return this.get(
      `promotions/${sellingCompanyCode}/${tourCode}/${departureCode}`
    )
  }

  promotion (sellingCompanyCode, tourCode, departureCode, promotionCode) {
    return this.get(
      encodeUrl`promotions/${sellingCompanyCode}/${tourCode}/${departureCode}/${promotionCode}`
    )
  }

  referral (sellingCompanyCode, tourCode, departureCode, referralCode) {
    return this.get(
      encodeUrl`referrals/code/${sellingCompanyCode}/${tourCode}/${departureCode}/${referralCode}`
    )
  }

  getReferralAdvocate (sellingCompanyCode, tourCode, departureCode, email, advocateId) {
    return this.post(
      encodeUrl`referrals/advocate/${sellingCompanyCode}/${tourCode}/${departureCode}`,
      {
        email,
        advocateId,
      }
    )
  }

  airPromotions ({
    departureCode,
    tourCode,
    sellingCompanyCode,
    user,
    flights,
  }) {
    return this.post(
      encodeUrl`promotions/air/automatic/${sellingCompanyCode}/${tourCode}/${departureCode}`,
      {
        user,
        flights,
      }
    )
  }
  getAirFareRules ({
    itinerary,
    fares,
    sellingCompanyCode,
    tokenId,
  }) {
    return this.post('flights/airfare-rules', {
      sellingCompanyCode,
      fares,
      itinerary,
      tokenId,
    })
  }

  travelAgentCommission (sellingCompanyCode, tourCode, departureCode) {
    return this.get(
      encodeUrl`travelAgent/commission/${sellingCompanyCode}/${tourCode}/${departureCode}`
    )
  }

  travelAgentDiscounts (sellingCompanyCode, tourCode, departureCode) {
    return this.get(encodeUrl`discounts/${sellingCompanyCode}/${tourCode}/${departureCode}`)
  }

  hotelAvailabilities (sellingCompanyCode, tourCode, departureCode) {
    return this.get(
      encodeUrl`${sellingCompanyCode}/${tourCode}/${departureCode}/hotelsAvailability`
    )
  }

  sendConfirmationEmail (bookingReference) {
    return this.post('/api/emails/booking-confirmation', {bookingReference})
  }

  async sendShareConfirmationEmail (bookingReference, emails) {
    const result =  await this.post('/api/emails/booking-confirmation', {
      bookingReference,
      shareEmails: emails,
    })
    return result
  }

  status (sellingCompanyCode, tourCode, departureCode) {
    return this.get(
      encodeUrl`${sellingCompanyCode}/${tourCode}/${departureCode}/status`
    ).then(body => {
      return {
        status: body.status,
        roomsOccupancy: body.roomsOccupancy,
      }
    })
  }

  countries () {
    if (this._countries) {
      return this._countries
    } else {
      return (this._countries = this.get('countries'))
    }
  }

  regions (country) {
    return this.get('regions/' + encodeURIComponent(country))
  }

  emailQuote (emailQuoteRequest) {
    return this.post('emailQuote', emailQuoteRequest)
  }

  airports () {
    return this.get('airports')
  }

  user (sellingCompanyCode) {
    return this.get(encodeUrl`user/${sellingCompanyCode}`)
  }

  logout ({ssoOnly = false} = {}) {
    return this.get(encodeUrl`logout?ssoOnly=` + ssoOnly)
  }

  handleAuth (path, token) {
    return this.post(path, {token}, {
      exceptions: response => response.statusCode >= 400 && ![401, 403].includes(response.statusCode),
      response: true,
    }).then(response => {
      const states = {
        200: {
          valid: true,
        },
        401: {
          valid: false,
        },
        403: {
          valid: true,
          ignore: true,
        },
      }

      return states[response.statusCode] || {}
    })
  }

  ssoLogin (token) {
    return this.handleAuth('ssoLogin', token)
  }

  authLogin (token) {
    return this.handleAuth('authLogin', token)
  }

  createBooking (request) {
    return this.client.put(`v2/book`, request, {exceptions: r => r.statusCode >= 500})
  }

  updateClientDetails (request) {
    return this.client.post(
      encodeUrl`amendClientDetails`,
      request,
      {exceptions: r => r.statusCode >= 500}
    )
  }

  updateClientBillingDetails (request) {
    return this.client.post(
      encodeUrl`amendClientBillingDetails`,
      request,
      {exceptions: r => r.statusCode >= 500}
    )
  }

  updatePaperDocs (request) {
    return this.client.post(
      encodeUrl`amendBooking`,
      request,
      {exceptions: r => r.statusCode >= 500}
    )
  }


  request (booking) {
    return this.client.post(encodeUrl`request`,{bookingReference: booking.bookingReference})
      .then(result => {
        return {
          confirmationEmailAddress: result.confirmationEmailAddress,
        }
      })
  }

  pay (request) {
    return this.client.post('v2/pay', request).then(result => {
      return {
        confirmationEmailAddress: result.confirmationEmailAddress,
      }
    })
  }

  confirmSale (request) {
    return this.client.post('confirm-sale', request)
  }

  hold (request) {
    return this.client.post('hold', request).then(result => {
      return {
        confirmationEmailAddress: result.confirmationEmailAddress,
      }
    })
  }

  promisedFlights ({
    sellingCompanyCode,
    fromAirport,
    departureDate,
    destinationAirport,
    returningAirport,
  } = {}) {
    const brand = sellingCompanyRegions.brand(sellingCompanyCode)
    const sellingRegion = sellingCompanyRegions.sellingRegion(sellingCompanyCode)

    return this.get(`/flights/promised/brand/${brand}/region/${sellingRegion}/price/from/${fromAirport}/on/${departureDate}/to/${destinationAirport}/returning-from/${returningAirport}`, {
      jsonReviver: dateJSONReviver,
      exceptions: response => response.statusCode >= 400 && ![404].includes(response.statusCode),
    })
  }

  searchFlights ({
    criteria,
    sellingCompanyCode,
    tourCode,
    departureCode,
    party,
    filterByDirectFlights = false,
  } = {}) {
    return this.post(`flights/${sellingCompanyCode}/${tourCode}/${departureCode}`,
      {
        criteria,
        party,
        filterByDirectFlights,
      }, {
        exceptions: response => response.statusCode > 400,
      }
    )
  }

  findFlight ({
    flightItineraryId,
    sellingCompanyCode,
    party,
    showPriceBreakdown,
  }) {
    return this.get(`single-flight/${sellingCompanyCode}?flightId=${flightItineraryId}&adults=${party.adults}&children=${party.children}&showPriceBreakdown=${showPriceBreakdown}`)
  }

  searchTransfers ({
    flightLegs,
    marketVariationCode,
    sellingCompanyCode,
    departureData,
    prePostAccommodation,
  }) {
    return this.post(`transfers/${sellingCompanyCode}/${marketVariationCode}`, {
      flightLegs,
      departureData,
      prePostAccommodation,
    })
  }
}

export default options => {
  return new Api(options)
}
